import './TicketList.scss';

import { DateField, List, useTable } from '@refinedev/antd';
import { BaseRecord } from '@refinedev/core';
import { Flex, Space } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import ProtectedCreateButton from '@/components/protected/ProtectedCreateButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import ProtectedShowButton from '@/components/protected/ProtectedShowButton';
import TicketStateTag from '@/components/ticket/TicketStateTag';
import { COMPANY_URLS, MACHINES_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { Ticket } from '@/model/Ticket/Ticket';
import { TICKET_STATE } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';

import TicketListFilter from './TicketFilter/TicketListFilter';

const TicketList = () => {
  const { pageSizePreference } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    sorters,
    setSorters,
    setCurrentPage: setCurrent,
    setPageSize,
    currentPage,
    pageSize,
  } = useTable<Ticket>({
    syncWithLocation: true,
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  // Persistencia de filtros y paginado del listado.
  useListStatePersistence({
    resource: RESOURCES.TICKETS,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={({ createButtonProps }) => (
        <>
          <TicketListFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
          <ProtectedCreateButton {...createButtonProps} />
        </>
      )}>
      <AppTable
        {...tableProps}
        rowKey="id"
        rowClassName={record => {
          const status: TICKET_STATE = record.status;
          const noTracking = record.no_tracking;

          return `TableRowTicket ${
            noTracking != undefined && noTracking
              ? 'NoTracking '
              : status == TICKET_STATE.OPEN || status == TICKET_STATE.REOPENED
                ? 'OpenTicket'
                : status == TICKET_STATE.RESOLVED
                  ? 'ResolvedTicket'
                  : ''
          }`;
        }}
        scroll={{ x: 1200, y: 'calc(100dvh - 310px)' }}>
        <AppTable.Column
          dataIndex="id"
          title="Id"
          width={80}
          render={value =>
            hasPermission('show') ? (
              <LinkTextField
                label={value}
                to={`/${RESOURCES.TICKETS}/${hasPermission('edit') ? 'edit' : 'show'}/${value}`}
                local
                withoutColor
              />
            ) : (
              value
            )
          }
        />
        <AppTable.Column dataIndex="ticket_title_str" title={t('Título')} width={250} />
        <AppTable.Column
          title={t('Empresa')}
          render={(_, record: Ticket) => {
            const { empresa, empresa_str } = record;

            return empresa || empresa_str ? (
              <LinkTextField
                label={empresa_str ? `${empresa_str} (${empresa})` : `(${empresa})`}
                to={`${COMPANY_URLS.ADMIN_EMPRESA}/${empresa}`}
                className="TableRowLink"
                admin
                withoutColor
              />
            ) : (
              '-'
            );
          }}
          width={250}
        />
        <AppTable.Column
          title={t('Móvil')}
          render={(_, record: Ticket) => {
            const { movil, movil_str } = record;

            return movil || movil_str ? (
              <LinkTextField
                label={movil_str ? `${movil_str} (${movil})` : `(${movil})`}
                to={`${MACHINES_URLS.ADMIN_MOVIL}/${movil}`}
                admin
                withoutColor
              />
            ) : (
              '-'
            );
          }}
          width={250}
        />
        <AppTable.Column
          dataIndex="assigned_to"
          title={t('Asignado')}
          render={value => (value ? `${value.first_name} ${value.last_name}` : '-')}
          width={180}
        />
        <AppTable.Column dataIndex="days_elapsed" title={t('Antigüedad')} width={110} />
        <AppTable.Column
          dataIndex="created"
          title={t('Fecha creación')}
          render={value => (value ? <DateField value={value} format="L" /> : '-')}
          width={140}
        />
        <AppTable.Column
          fixed="right"
          dataIndex="status"
          title={t('Estado')}
          render={value => (
            <Flex align="center" justify="center">
              <TicketStateTag ticketState={value} />
            </Flex>
          )}
          width={110}
        />
        {(hasPermission('edit') || hasPermission('show')) && (
          <AppTable.Column
            dataIndex="actions"
            width={80}
            fixed="right"
            render={(_, record: BaseRecord) => (
              <Space>
                {hasPermission('edit') && (
                  <ProtectedEditButton hideText size="small" recordItemId={record.id} />
                )}
                {hasPermission('show') && (
                  <ProtectedShowButton hideText size="small" recordItemId={record.id} />
                )}
              </Space>
            )}
          />
        )}
      </AppTable>
    </List>
  );
};

export default TicketList;
